import { useRef, useState } from "react";
import { Settings2, X, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Column {
  key: string;
  label: string;
}

interface TableSettingsGearProps {
  columns: Column[];
  visibleColumns: Set<string>;
  onToggle: (key: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function TableColumnsPanel({
  columns,
  visibleColumns,
  onToggle,
  onClose,
  onReset,
  onReorder,
}: {
  columns: Column[];
  visibleColumns: Set<string>;
  onToggle: (key: string) => void;
  onClose?: () => void;
  onReset?: () => void;
  onReorder?: (orderedKeys: string[]) => void;
}) {
  const dragIndex = useRef<number | null>(null);
  const [dragHiddenKey, setDragHiddenKey] = useState<string | null>(null);

  const handleDragStart = (idx: number, key: string) => {
    dragIndex.current = idx;
    requestAnimationFrame(() => setDragHiddenKey(key));
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIndex.current === null || dragIndex.current === idx) return;
    const next = [...columns];
    const [moved] = next.splice(dragIndex.current, 1);
    next.splice(idx, 0, moved);
    dragIndex.current = idx;
    onReorder?.(next.map((c) => c.key));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragIndex.current = null;
    setDragHiddenKey(null);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm animate-in fade-in-0 slide-in-from-top-2 duration-200">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Table Columns</p>
        <div className="flex items-center gap-1">
          {onReset && (
            <Button variant="ghost" size="sm" className="h-6 text-[11px] px-2 text-muted-foreground hover:text-foreground" onClick={onReset}>
              Reset
            </Button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-md bg-secondary hover:bg-secondary/80 transition-colors text-foreground border border-border"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {columns.map((col, idx) => {
          const isActive = visibleColumns.has(col.key);
          return (
            <div
              key={col.key}
              draggable={!!onReorder}
              onDragStart={() => handleDragStart(idx, col.key)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={handleDrop}
              onDragEnd={() => { dragIndex.current = null; setDragHiddenKey(null); }}
              className={cn(
                "flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 select-none",
                onReorder ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
                isActive
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:bg-secondary/60",
                dragHiddenKey === col.key && "opacity-0"
              )}
            >
              {onReorder && (
                <GripVertical className={cn("w-3 h-3 shrink-0", isActive ? "text-primary-foreground/60" : "text-muted-foreground/60")} />
              )}
              <button type="button" onClick={() => onToggle(col.key)} className="outline-none">
                {col.label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function TableSettingsGear({ columns, visibleColumns, onToggle, open, onOpenChange }: TableSettingsGearProps) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 text-gray-500 hover:text-gray-900 hover:bg-gray-300/50 ml-auto"
      onClick={() => onOpenChange?.(!open)}
    >
      <Settings2 className="w-4 h-4" />
    </Button>
  );
}
